import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrandMark } from "@/components/Brand";
import { TypeProfileForm } from "@/components/TypeProfileForm";

function StatusBadge({ s, id }: { s: any; id: string }) {
  const label = s.complete ? "Complete" : s.consented ? `${s.missing.length} new question${s.missing.length === 1 ? "" : "s"}` : "Not started";
  return <span className={`rounded-sm px-2 py-0.5 text-[11px] font-semibold ${s.complete ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid={`event-profile-status-${id}`}>{label}</span>;
}

export default function EventProfiles() {
  const { customer } = useAuth();
  const [items, setItems] = useState<any[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = () => api.get("/me/event-profiles").then((r) => setItems(r.data));
  useEffect(() => { if (customer) load(); }, [customer]);

  if (customer === undefined) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!customer) return <Navigate to="/attendee" replace />;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3">
          <BrandMark />
          <Link to="/me" className="inline-flex items-center gap-1 text-sm font-semibold" data-testid="event-profiles-back-link"><ArrowLeft className="h-4 w-4" /> My vendors</Link>
        </div>
      </header>
      <main className="mx-auto max-w-md space-y-4 px-4 py-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">My event profiles</h1>
          <p className="mt-1 text-sm text-slate-600">Answer once per event type. When you scan a booth at that kind of event, your answers go straight to that vendor. Changes apply to vendors you've already shared with too.</p>
        </div>
        {items === null && <Loader2 className="mx-auto h-5 w-5 animate-spin" />}
        {items?.map((it) => {
          const t = it.event_type;
          const isOpen = open === t.id;
          return (
            <div key={t.id} className="rounded-2xl border bg-white" data-testid={`event-profile-card-${t.id}`}>
              <button className="flex w-full items-center gap-3 p-4 text-left" onClick={() => setOpen(isOpen ? null : t.id)} data-testid={`event-profile-toggle-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.description}</p>
                </div>
                <StatusBadge s={it.status} id={t.id} />
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="border-t p-4">
                  <TypeProfileForm eventType={t} answers={it.answers} consented={it.status.consented} consentText={it.consent_text}
                    onSaved={() => { toast.success(`${t.name} answers saved`); load(); }} />
                </div>
              )}
            </div>
          );
        })}
        {items?.length === 0 && <p className="text-sm text-slate-500">No event types yet.</p>}
        {items?.some((i) => i.status.complete) && <p className="flex items-center gap-1.5 text-xs text-slate-500"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Only vendors whose booths you scan get your answers.</p>}
      </main>
    </div>
  );
}
